/**
 * Gallery Interactive Panel Component
 * Manages the unified container for filter controls and carousel
 * Handles click-outside behavior based on filter modal state
 */
export function galleryInteractivePanel() {
  return {
    panel_visible: false,
    filter_modal_open: false,
    carousel_ready: false,
    _carouselPrimed: false,
    search_query: '',
    search_service: null,

    init() {
      this.$watch(() => this.$store.galleryUI.active_artwork_index, (index) => {
        this.panel_visible = index !== null;

        if (index !== null) {
          setTimeout(() => {
            this.carousel_ready = true;
          }, 50);
          this.primeCarousel('panel-activate');
        } else {
          this.carousel_ready = false;
        }
      });

      this.$watch(() => this.$store.galleryUI.filter_modal_open, (isOpen) => {
        this.filter_modal_open = isOpen;
      });

      this.panel_visible = this.$store.galleryUI.active_artwork_index !== null;
      this.filter_modal_open = this.$store.galleryUI.filter_modal_open;

      setTimeout(() => {
        this.initializeSearch();
      }, 500);

      this.$watch('filtered_artworks', (newArtworks) => {
        if (!this._carouselPrimed && Array.isArray(newArtworks) && newArtworks.length > 0) {
          this._carouselPrimed = true;
          this.primeCarousel('initial-products');
        }
      }, { immediate: true, deep: false });

      this.$watch('orientation', (newOrientation, oldOrientation) => {
        if (!oldOrientation || newOrientation === oldOrientation) {
          return;
        }

        this._carouselPrimed = false;

        const hasArtworks = Array.isArray(this.filtered_artworks) && this.filtered_artworks.length > 0;
        if (hasArtworks) {
          this._carouselPrimed = true;
          this.primeCarousel('orientation-change', true);
        }
      });

      if (typeof window !== 'undefined') {
        const schedulePrime = () => this.primeCarousel('idle');
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(schedulePrime, { timeout: 600 });
        } else {
          setTimeout(schedulePrime, 300);
        }
      }

      if (!this._handleSearchCleared) {
        this._handleSearchCleared = () => {
          this.search_query = '';
          if (this.$refs.searchInput) {
            this.$refs.searchInput.value = '';
          }
        };
        document.addEventListener('gallery:search-cleared', this._handleSearchCleared);
      }
    },

    destroy() {
      if (this._handleSearchCleared) {
        document.removeEventListener('gallery:search-cleared', this._handleSearchCleared);
        this._handleSearchCleared = null;
      }
    },

    async initializeSearch() {
      if (!window.galleryBoostBridge) {
        return;
      }

      const { GallerySearchService } = await import('../services/search/gallery-search-service.js');

      const stores = {
        products: this.$store.galleryProducts,
        ui: this.$store.galleryUI,
        core: this.$store.galleryCore
      };

      if (!window.gallerySearchService) {
        window.gallerySearchService = new GallerySearchService(
          window.galleryBoostBridge,
          stores
        );
      } else {
        window.gallerySearchService.updateStores(stores);
      }

      this.search_service = window.gallerySearchService;
    },

    handleSearchInput(event) {
      const query = event.target.value;
      this.search_query = query;

      if (query.length >= 2) {
        this.pulseSearchIcon();
        this.showEstimatedResults(query);

        if (this.$refs.searchInput) {
          this.$refs.searchInput.classList.add('gallery-filter-controls__search-input--active');
        }
      } else if (query.length === 0) {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.classList.remove('gallery-filter-controls__search-input--active');
        }
      }

      if (this.search_service) {
        this.search_service.handleSearchInput(query);
      }
    },


    pulseSearchIcon() {
      const icon = this.$el.querySelector('.gallery-filter-controls__icon--search');
      if (icon) {
        icon.classList.remove('pulse-animation');
        void icon.offsetWidth;
        icon.classList.add('pulse-animation');
        setTimeout(() => icon.classList.remove('pulse-animation'), 600);
      }
    },

    showEstimatedResults(query) {
      const estimates = {
        2: 150,
        3: 80,
        4: 40,
        5: 20,
        default: 15
      };

      const estimate = estimates[Math.min(query.length, 5)] || estimates.default;

      if (this.$store.galleryUI) {
        this.$store.galleryUI.search_result_count = estimate;

        if (this.$store.galleryProducts) {
          this.$store.galleryProducts.search_result_count = estimate;
        }
      }
    },

    executeSearch() {
      if (this.search_service && this.search_query) {
        this.search_service.executeSearch(this.search_query);
      }
    },

    clearSearch() {
      this.search_query = '';
      if (this.search_service) {
        this.search_service.clearSearch();
      }

      if (this.$refs.searchInput) {
        this.$refs.searchInput.value = '';
        this.$refs.searchInput.focus();
      }
    },

    handleSearchFocus() {
      if (this.$store.galleryUI) {
        this.$store.galleryUI.search_focused = true;
      }
    },

    handleSearchBlur() {
      if (this.$store.galleryUI) {
        this.$store.galleryUI.search_focused = false;
      }
    },

    handleClickOutside(event) {
      if (this.filter_modal_open) {
        return;
      }

      if (this.$store.galleryUI.interactive_panel_locked) {
        return;
      }

      const filterModal = document.querySelector('.gallery-filter-modal');
      const boostOverlay = document.querySelector('.boost-sd__filter-tree-vertical-sticky-overlay');

      if (
        (filterModal && filterModal.contains(event.target)) ||
        (boostOverlay && boostOverlay.contains(event.target))
      ) {
        return;
      }

      if (event.target.closest('.gallery-filter-modal') ||
          event.target.closest('.boost-sd__filter-tree-vertical-sticky')) {
        return;
      }

      this.closePanel();
    },

    closePanel() {
      if (!this.$store.galleryUI.interactive_panel_locked) {
        this.$store.galleryUI.setActiveArtwork(null);
      }
    },

    primeCarousel(source = 'auto', force = false) {
      const carouselEl = this.$refs.carousel;
      if (!carouselEl) {
        return;
      }

      const hasArtworks = Array.isArray(this.filtered_artworks) && this.filtered_artworks.length > 0;
      if (!hasArtworks && !force) {
        return;
      }

      const event = new CustomEvent('gallery:prime-carousel', {
        detail: {
          source,
          force,
          orientation: this.orientation
        },
        bubbles: true
      });

      carouselEl.dispatchEvent(event);
    },

    openFilterModal() {
      this.$store.galleryUI.filter_modal_open = true;
    },

    closeFilterModal() {
      this.$store.galleryUI.filter_modal_open = false;
    },

    get works_count_display() {
      return this.$store.galleryProducts.works_count_display;
    },

    get has_active_filters() {
      return this.$store.galleryProducts.has_active_filters;
    },

    get filter_count_display() {
      return this.$store.galleryProducts.filter_count_display;
    },

    get totalActiveFilters() {
      return this.$store.galleryProducts.boost_filter_count;
    },

    get filtered_artworks() {
      return this.$store.galleryProducts.filtered_artworks;
    },

    get selected_artworks() {
      return this.$store.galleryCore.selected_artworks;
    },

    get ui_state() {
      return {
        active_artwork_index: this.$store.galleryUI.active_artwork_index,
        hover_artwork_index: this.$store.galleryUI.hover_artwork_index
      };
    },

    get orientation() {
      return this.$store.galleryCore.orientation;
    }
  };
}

// Register with Alpine
document.addEventListener('alpine:init', () => {
  Alpine.data('galleryInteractivePanel', galleryInteractivePanel);
});
